import React, { useState, useEffect, useCallback } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const EMPTY_FORM = {
  id: null,
  name: "",
  whatsappIds: [],
  userIds: [],
  autoAssignMode: "off",
  autoAssignDelaySeconds: 60,
  autoAssignUserIds: [],
};

const FunnelManageDialog = ({ open, onClose, onChanged }) => {
  const [funnels, setFunnels] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadFunnels = useCallback(async () => {
    try {
      const { data } = await api.get("/funnels", { params: { manage: 1 } });
      setFunnels(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(null);
    loadFunnels();
    api.get("/whatsapp").then(({ data }) => setWhatsapps(data)).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
  }, [open, loadFunnels]);

  const funnelOfWhatsapp = whatsappId =>
    funnels.find(
      f => f.id !== form?.id && f.whatsapps.some(w => w.id === whatsappId)
    );

  const handleEdit = funnel =>
    setForm({
      id: funnel.id,
      name: funnel.name,
      whatsappIds: funnel.whatsapps.map(w => w.id),
      userIds: (funnel.users || []).map(u => u.id),
      autoAssignMode: funnel.autoAssignMode || "off",
      autoAssignDelaySeconds: funnel.autoAssignDelaySeconds || 60,
      autoAssignUserIds: funnel.autoAssignUserIds || [],
    });

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Ponle un nombre al embudo.");
      return;
    }
    if (form.autoAssignMode === "single" && form.autoAssignUserIds.length !== 1) {
      toast.error("Elige un ejecutivo para la asignación automática.");
      return;
    }
    if (form.autoAssignMode === "parity" && form.autoAssignUserIds.length < 2) {
      toast.error("Elige al menos 2 ejecutivos para repartir en paridad.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        whatsappIds: form.whatsappIds,
        userIds: form.userIds,
        autoAssignMode: form.autoAssignMode,
        autoAssignDelaySeconds: form.autoAssignDelaySeconds,
        autoAssignUserIds: form.autoAssignMode === "off" ? [] : form.autoAssignUserIds,
      };
      if (form.id) {
        await api.put(`/funnels/${form.id}`, payload);
        toast.success("Embudo actualizado.");
      } else {
        await api.post("/funnels", payload);
        toast.success("Embudo creado.");
      }
      setForm(null);
      await loadFunnels();
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async funnel => {
    if (
      !window.confirm(
        `¿Eliminar el embudo "${funnel.name}"? Sus columnas se borran y los tickets vuelven a "Inicio". Las conexiones quedan sin embudo hasta que las asignes a otro.`
      )
    )
      return;
    try {
      await api.delete(`/funnels/${funnel.id}`);
      toast.success("Embudo eliminado.");
      await loadFunnels();
      onChanged();
    } catch (err) {
      toastError(err);
    }
  };

  const setList = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value.map(Number) }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Gestionar embudos</DialogTitle>
      <DialogContent dividers>
        {form ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <TextField
              label="Nombre del embudo"
              variant="outlined"
              size="small"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              autoFocus
            />

            <FormControl variant="outlined" size="small">
              <InputLabel>Conexiones</InputLabel>
              <Select
                multiple
                label="Conexiones"
                value={form.whatsappIds}
                onChange={e => setList("whatsappIds", e.target.value)}
                renderValue={selected =>
                  whatsapps
                    .filter(w => selected.includes(w.id))
                    .map(w => w.name)
                    .join(", ")
                }
              >
                {whatsapps.map(w => {
                  const other = funnelOfWhatsapp(w.id);
                  return (
                    <MenuItem key={w.id} value={w.id}>
                      <Checkbox
                        size="small"
                        checked={form.whatsappIds.includes(w.id)}
                      />
                      <ListItemText
                        primary={w.name}
                        secondary={other ? `Hoy en "${other.name}" (se moverá)` : null}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small">
              <InputLabel>Asignación automática</InputLabel>
              <Select
                label="Asignación automática"
                value={form.autoAssignMode}
                onChange={e =>
                  setForm({
                    ...form,
                    autoAssignMode: e.target.value,
                    autoAssignUserIds: [],
                  })
                }
              >
                <MenuItem value="off">Desactivada</MenuItem>
                <MenuItem value="single">Asignar ejecutivo automático</MenuItem>
                <MenuItem value="parity">Asignar en paridad</MenuItem>
              </Select>
            </FormControl>

            {form.autoAssignMode === "single" && (
              <>
                <FormControl variant="outlined" size="small">
                  <InputLabel>Ejecutivo</InputLabel>
                  <Select
                    label="Ejecutivo"
                    value={form.autoAssignUserIds[0] || ""}
                    onChange={e =>
                      setForm({
                        ...form,
                        autoAssignUserIds: [Number(e.target.value)],
                      })
                    }
                  >
                    {users.map(u => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Segundos antes de asignar automático"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={form.autoAssignDelaySeconds}
                  onChange={e =>
                    setForm({
                      ...form,
                      autoAssignDelaySeconds: Number(e.target.value) || 60,
                    })
                  }
                  inputProps={{ min: 5 }}
                />
                <Typography variant="caption" color="textSecondary">
                  Si nadie le da &ldquo;Aceptar&rdquo; al ticket en ese
                  tiempo, se asigna solo a este ejecutivo (igual que si lo
                  hubiera aceptado a mano).
                </Typography>
              </>
            )}

            {form.autoAssignMode === "parity" && (
              <>
                <FormControl variant="outlined" size="small">
                  <InputLabel>Ejecutivos (mínimo 2)</InputLabel>
                  <Select
                    multiple
                    label="Ejecutivos (mínimo 2)"
                    value={form.autoAssignUserIds}
                    onChange={e => setList("autoAssignUserIds", e.target.value)}
                    renderValue={selected =>
                      users
                        .filter(u => selected.includes(u.id))
                        .map(u => u.name)
                        .join(", ")
                    }
                  >
                    {users.map(u => (
                      <MenuItem key={u.id} value={u.id}>
                        <Checkbox
                          size="small"
                          checked={form.autoAssignUserIds.includes(u.id)}
                        />
                        <ListItemText primary={u.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="textSecondary">
                  Cada lead nuevo sin asignar se reparte por turno entre
                  estos ejecutivos apenas llega, sin espera.
                </Typography>
              </>
            )}

            <FormControl variant="outlined" size="small">
              <InputLabel>Usuarios con acceso</InputLabel>
              <Select
                multiple
                label="Usuarios con acceso"
                value={form.userIds}
                onChange={e => setList("userIds", e.target.value)}
                renderValue={selected =>
                  users
                    .filter(u => selected.includes(u.id))
                    .map(u => u.name)
                    .join(", ")
                }
              >
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    <Checkbox size="small" checked={form.userIds.includes(u.id)} />
                    <ListItemText primary={u.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="caption" color="textSecondary">
              Cada conexión pertenece a un solo embudo: sus tickets aparecen
              solo ahí. Un embudo nuevo nace con las columnas Inicio y
              Contactado; el resto las creas desde el propio embudo.
            </Typography>
          </div>
        ) : (
          <>
            <List dense>
              {funnels.map(f => (
                <ListItem key={f.id} divider>
                  <ListItemText
                    primary={f.name}
                    secondaryTypographyProps={{ component: "div" }}
                    secondary={
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                        {f.whatsapps.map(w => (
                          <Chip key={`w${w.id}`} size="small" label={w.name} color="primary" variant="outlined" />
                        ))}
                        {(f.users || []).map(u => (
                          <Chip key={`u${u.id}`} size="small" label={u.name} />
                        ))}
                        {f.autoAssignMode === "single" && (
                          <Chip
                            size="small"
                            color="secondary"
                            label="Asignación automática"
                          />
                        )}
                        {f.autoAssignMode === "parity" && (
                          <Chip
                            size="small"
                            color="secondary"
                            label="Asignación en paridad"
                          />
                        )}
                        {!f.whatsapps.length && (
                          <Typography variant="caption" color="error">
                            Sin conexiones
                          </Typography>
                        )}
                      </div>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handleEdit(f)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(f)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {!funnels.length && (
                <Typography variant="body2" color="textSecondary">
                  Aún no hay embudos.
                </Typography>
              )}
            </List>
            <Button
              startIcon={<AddIcon />}
              color="primary"
              onClick={() => setForm({ ...EMPTY_FORM })}
              style={{ marginTop: 8, textTransform: "none" }}
            >
              Nuevo embudo
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {form ? (
          <>
            <Button onClick={() => setForm(null)} disabled={saving}>
              Volver
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              Guardar
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Cerrar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FunnelManageDialog;
